/**
 * @file: useReservationStats.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The summary-tile counts (total / upcoming / arrived / converted), fetched in a
 * single grouped `stats` request that honors the active search (not the status
 * tab) so every tile keeps its own total while a status is selected.
 */

import {
  ReservationListFilter,
  ReservationStats,
} from '@euler/model/reservation/types';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useState } from 'react';

export const useReservationStats = (
  searchFilter: ReservationListFilter,
  // Bumped by the caller (e.g. on screen focus) to force a recount after a
  // detail-screen mutation moved a reservation between status buckets.
  reloadKey?: number,
): ReservationStats | undefined => {
  const { reservationService } = useServiceFactory();
  const [stats, setStats] = useState<ReservationStats | undefined>(undefined);

  // Key the effect on the serialized search slice so a structurally-identical
  // filter (rebuilt every render) doesn't retrigger the request.
  const filterKey = JSON.stringify(searchFilter);
  useEffect(() => {
    let cancelled = false;
    const base: ReservationListFilter = JSON.parse(filterKey);
    void (async () => {
      try {
        const next = await reservationService.stats(base);
        if (!cancelled) setStats(next);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reservationService, filterKey, reloadKey]);

  return stats;
};
